import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Languages, Sparkles, ArrowLeft } from 'lucide-react';
import TheoryRenderer from '../../components/course/TheoryRenderer';

// FormattingHelpPage
// ──────────────────
// Справка по тому как писать тексты в редакторах теории и квизов.
// Двуязычная (RU + KK). Каждый блок: левая колонка — что писать,
// правая — как это будет выглядеть.
//
// Доступна и студентам, и инструкторам, и админам — формат markdown
// один и тот же везде в проекте (мы используем TheoryRenderer всюду).
//
// Зачем нужна: пользователи не догадаются что в полях квиза/теории
// можно писать формулы $x^2 = y$ и жирный **текст**. Эта страница —
// единая точка обучения, ссылка на неё стоит из шапки редактора.

const CONTENT = {
  ru: {
    title: 'Как форматировать тексты',
    subtitle: 'Поддерживается во всех редакторах: теория, квиз, описание',
    sections: [
      {
        title: 'Жирный, курсив',
        items: [
          { src: '**жирный текст**', out: '**жирный текст**' },
          { src: '_курсив_', out: '_курсив_' },
          { src: '**жирный** и _курсив_ одновременно', out: '**жирный** и _курсив_ одновременно' },
        ],
      },
      {
        title: 'Заголовки',
        items: [
          { src: '# Большой заголовок', out: '# Большой заголовок' },
          { src: '## Подзаголовок', out: '## Подзаголовок' },
          { src: '### Маленький заголовок', out: '### Маленький заголовок' },
        ],
      },
      {
        title: 'Списки',
        items: [
          { src: '- первый пункт\n- второй пункт\n- третий пункт', out: '- первый пункт\n- второй пункт\n- третий пункт' },
          { src: '1. Сначала\n2. Потом\n3. Наконец', out: '1. Сначала\n2. Потом\n3. Наконец' },
        ],
      },
      {
        title: 'Код',
        items: [
          { src: 'В строке: `const x = 5`', out: 'В строке: `const x = 5`' },
          {
            src: '```\nfunction hello() {\n  return "hi";\n}\n```',
            out: '```\nfunction hello() {\n  return "hi";\n}\n```',
          },
        ],
      },
      {
        title: 'Математические формулы (KaTeX)',
        items: [
          { src: 'В строке: $a^2 + b^2 = c^2$', out: 'В строке: $a^2 + b^2 = c^2$' },
          { src: 'Дроби: $\\frac{1}{2}$ и $\\frac{x+1}{x-1}$', out: 'Дроби: $\\frac{1}{2}$ и $\\frac{x+1}{x-1}$' },
          { src: 'Степени и индексы: $x^{2n} + y_{i,j}$', out: 'Степени и индексы: $x^{2n} + y_{i,j}$' },
          { src: 'Корни: $\\sqrt{x}$ и $\\sqrt[3]{8}$', out: 'Корни: $\\sqrt{x}$ и $\\sqrt[3]{8}$' },
          {
            src: 'Блок-формула:\n$$\n\\int_0^1 x^2 \\, dx = \\frac{1}{3}\n$$',
            out: 'Блок-формула:\n$$\n\\int_0^1 x^2 \\, dx = \\frac{1}{3}\n$$',
          },
          {
            src: 'Сумма:\n$$\n\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}\n$$',
            out: 'Сумма:\n$$\n\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}\n$$',
          },
        ],
      },
      {
        title: 'Вставка из Word / Google Docs',
        items: [
          {
            src: 'Можно копировать текст из Word, Google Docs, PDF — всё придёт как чистый текст без сломанного форматирования. Затем вручную выделите нужные слова **жирным** через **двойные звёздочки**.',
            out: 'Можно копировать текст из Word, Google Docs, PDF — всё придёт как чистый текст без сломанного форматирования. Затем вручную выделите нужные слова **жирным** через **двойные звёздочки**.',
          },
        ],
      },
    ],
  },
  kk: {
    title: 'Мәтінді қалай форматтау керек',
    subtitle: 'Барлық редакторларда жұмыс істейді: теория, тест, сипаттама',
    sections: [
      {
        title: 'Қалың, көлбеу',
        items: [
          { src: '**қалың мәтін**', out: '**қалың мәтін**' },
          { src: '_көлбеу_', out: '_көлбеу_' },
          { src: '**қалың** және _көлбеу_ бірге', out: '**қалың** және _көлбеу_ бірге' },
        ],
      },
      {
        title: 'Тақырыптар',
        items: [
          { src: '# Үлкен тақырып', out: '# Үлкен тақырып' },
          { src: '## Кіші тақырып', out: '## Кіші тақырып' },
          { src: '### Ең кіші тақырып', out: '### Ең кіші тақырып' },
        ],
      },
      {
        title: 'Тізімдер',
        items: [
          { src: '- бірінші пункт\n- екінші пункт\n- үшінші пункт', out: '- бірінші пункт\n- екінші пункт\n- үшінші пункт' },
          { src: '1. Алдымен\n2. Содан кейін\n3. Соңында', out: '1. Алдымен\n2. Содан кейін\n3. Соңында' },
        ],
      },
      {
        title: 'Код',
        items: [
          { src: 'Жолда: `const x = 5`', out: 'Жолда: `const x = 5`' },
          {
            src: '```\nfunction hello() {\n  return "hi";\n}\n```',
            out: '```\nfunction hello() {\n  return "hi";\n}\n```',
          },
        ],
      },
      {
        title: 'Математикалық формулалар (KaTeX)',
        items: [
          { src: 'Жолда: $a^2 + b^2 = c^2$', out: 'Жолда: $a^2 + b^2 = c^2$' },
          { src: 'Бөлшектер: $\\frac{1}{2}$ және $\\frac{x+1}{x-1}$', out: 'Бөлшектер: $\\frac{1}{2}$ және $\\frac{x+1}{x-1}$' },
          { src: 'Дәреже мен индекс: $x^{2n} + y_{i,j}$', out: 'Дәреже мен индекс: $x^{2n} + y_{i,j}$' },
          { src: 'Түбірлер: $\\sqrt{x}$ және $\\sqrt[3]{8}$', out: 'Түбірлер: $\\sqrt{x}$ және $\\sqrt[3]{8}$' },
          {
            src: 'Блок формула:\n$$\n\\int_0^1 x^2 \\, dx = \\frac{1}{3}\n$$',
            out: 'Блок формула:\n$$\n\\int_0^1 x^2 \\, dx = \\frac{1}{3}\n$$',
          },
          {
            src: 'Қосынды:\n$$\n\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}\n$$',
            out: 'Қосынды:\n$$\n\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}\n$$',
          },
        ],
      },
      {
        title: 'Word / Google Docs-тан көшіру',
        items: [
          {
            src: 'Word, Google Docs, PDF-тан мәтін көшіруге болады — бәрі таза мәтін ретінде келеді, форматтау бұзылмайды. Содан кейін керекті сөздерді **қалың** етіп қою үшін қос жұлдыз қойыңыз.',
            out: 'Word, Google Docs, PDF-тан мәтін көшіруге болады — бәрі таза мәтін ретінде келеді, форматтау бұзылмайды. Содан кейін керекті сөздерді **қалың** етіп қою үшін қос жұлдыз қойыңыз.',
          },
        ],
      },
    ],
  },
};

const FormattingHelpPage = () => {
  const navigate = useNavigate();
  // Дефолт — русский. Юзер один раз переключает, мы запоминаем в localStorage.
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem('formatting-help-lang') || 'ru';
    } catch {
      return 'ru';
    }
  });

  const changeLang = (l) => {
    setLang(l);
    try { localStorage.setItem('formatting-help-lang', l); } catch { /* ignore */ }
  };

  const c = CONTENT[lang];

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Back navigation. navigate(-1) возвращает на предыдущую страницу
          — обычно это редактор где юзер писал квиз/теорию. Раньше открывали
          в новой вкладке (target=_blank), но юзеры теряли вкладку и думали
          что они "потеряли куиз". Теперь — обычный навигейт + Back. */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
      >
        <ArrowLeft className="w-4 h-4" />
        Назад
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {c.title}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {c.subtitle}
          </p>
        </div>

        {/* Language toggle */}
        <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1">
          <button
            type="button"
            onClick={() => changeLang('ru')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              lang === 'ru'
                ? 'bg-indigo-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <Languages className="w-3.5 h-3.5" /> Русский
          </button>
          <button
            type="button"
            onClick={() => changeLang('kk')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              lang === 'kk'
                ? 'bg-indigo-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <Languages className="w-3.5 h-3.5" /> Қазақша
          </button>
        </div>
      </div>

      {/* Sections */}
      {c.sections.map((section, idx) => (
        <section
          key={idx}
          className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
            <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              {section.title}
            </h2>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {section.items.map((item, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div className="p-4 sm:p-5 bg-gray-50/50 dark:bg-gray-900/40 border-r-0 md:border-r border-b md:border-b-0 border-gray-100 dark:border-gray-800">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                    {lang === 'ru' ? 'Что писать' : 'Не жазу керек'}
                  </div>
                  <pre className="text-xs sm:text-sm font-mono whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-950 rounded-lg p-3 border border-gray-200 dark:border-gray-800">
                    {item.src}
                  </pre>
                </div>
                <div className="p-4 sm:p-5">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                    {lang === 'ru' ? 'Как выглядит' : 'Қалай көрінеді'}
                  </div>
                  <div className="bg-white dark:bg-gray-950 rounded-lg p-3 border border-gray-200 dark:border-gray-800">
                    <TheoryRenderer content={item.out} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-4 text-sm text-amber-900 dark:text-amber-100">
        {lang === 'ru' ? (
          <>
            💡 <b>Совет:</b> в каждом редакторе есть кнопка <b>«Превью»</b> —
            переключитесь чтобы увидеть как будет выглядеть текст для студента.
          </>
        ) : (
          <>
            💡 <b>Кеңес:</b> әр редакторда <b>«Превью»</b> түймесі бар — оны
            басып студентке мәтін қалай көрінетінін көре аласыз.
          </>
        )}
      </div>
    </div>
  );
};

export default FormattingHelpPage;
